import fp from 'lodash/fp';
import React, { useMemo } from 'react';
import { formatDate } from '@factorin/util';

import { useSelector, useAction } from 'Modules/core/redux';
import { Text, Heading, Button, cn, Certificate, TrashCan, useMemoDeps } from 'Modules/ui';
import { modalClassNames } from 'Modules/core/messaging';
import { useTranslation } from 'Modules/core/i18n';
import { FileSection, FileItem } from 'Modules/business/fapp';
import { COModal } from 'Modules/business/uiCo/components/ClientOfficeModal';

import { cryptoprofilesActions } from '../../state/cryptoprofiles/actions';
import { CertificateSelector } from '../CertificateSelector';
import styles from './index.module.scss';


const renderRemoveButton = id => function RemoveButton({ className }) {
    const { t } = useTranslation('cryptoProfilesManager');

    const removeCertificate = useAction(
        () => cryptoprofilesActions.remove(id),
        [],
    );

    return (
        <Button
            variant='auxiliary'
            onClick={removeCertificate}
            className={cn(className, styles.button)}
            title={t('buttons.remove')}
        >
            <TrashCan />
        </Button>
    );
};

const CertificateComponent = ({
    certificate: { Id, Thumbprint, SubjectCommonName, NotAfter },
    readonly,
    ...props
}) => {
    const { t } = useTranslation('cryptoProfilesManager');

    const button = useMemoDeps(renderRemoveButton, [Id]);

    return (
        <FileItem
            key={Thumbprint}
            icon={Certificate}
            description={t('labels.certificateEntry', {
                subject: SubjectCommonName,
                date: formatDate(NotAfter),
            })}
            button={!readonly && button}
            {...props}
        />
    );
};

const Certificates = ({ readonly }) => {
    const cryptoprofiles = useSelector('cryptoprofiles.profiles');
    const { t } = useTranslation('cryptoProfilesManager');

    const [deleted, registered] = useMemo(
        () => fp.partition(fp.get('IsDeleted'), cryptoprofiles || []),
        [cryptoprofiles],
    );

    return (
        <>
            <Heading component='h3' headingType='h3'>
                {t('labels.registered')}
            </Heading>
            {fp.isEmpty(registered)
                ? <Text component='p'>{t('labels.emptyList')}</Text>
                : (
                    <FileSection className={styles.certsList}>
                        {fp.map(data => (
                            <CertificateComponent
                                key={data.Thumbprint}
                                certificate={data}
                                readonly={readonly}
                            />
                        ), registered)}
                    </FileSection>
                )}
            {!fp.isEmpty(deleted) && (
                <>
                    <Heading component='h3' headingType='h3'>
                        {t('labels.deleted')}
                    </Heading>
                    <FileSection className={styles.certsList}>
                        {fp.map(data => (
                            <CertificateComponent
                                key={data.Thumbprint}
                                certificate={data}
                                readonly
                            />
                        ), deleted)}
                    </FileSection>
                </>
            )}
        </>
    );
};

const CryptoProfilesManagerComponent = () => {
    const certificate = useSelector('certificates.addToProfile.selected');
    const registerCertificate = useAction(
        () => cryptoprofilesActions.registerCert({ certificate }),
        [certificate],
    );

    const { t } = useTranslation('cryptoProfilesManager');

    return (
        <COModal title={t('labels.title')}>
            <section className={cn(modalClassNames.content, styles.content)}>
                <Certificates />
                <Heading component='h3' headingType='h3' className={styles.selectorHeader}>
                    {t('labels.available')}
                </Heading>
                <CertificateSelector path='addToProfile' />
                <div className={modalClassNames.buttons}>
                    <Button
                        variant='accent'
                        className={modalClassNames.button}
                        disabled={!certificate}
                        onClick={registerCertificate}
                    >
                        {t('buttons.register')}
                    </Button>
                </div>
            </section>
        </COModal>
    );
};

const ReadOnlyCryptoProfilesManagerComponent = () => {
    const { t } = useTranslation('cryptoProfilesManager');

    return (
        <COModal className={styles.modalSize} title={t('labels.readonlyTitle')}>
            <section className={styles.content}>
                <Certificates readonly />
            </section>
        </COModal>
    );
};

export const CryptoProfilesManager = {
    component: CryptoProfilesManagerComponent,
};

export const ReadOnlyCryptoProfilesManager = {
    component: ReadOnlyCryptoProfilesManagerComponent,
};
